import React, { useRef } from 'react';

import { Field } from '@buildoutinc/blueprint-react/ui/Field';
import { Input } from '@buildoutinc/blueprint-react/ui/Input';

import { Alert } from 'components/ui/alert';
import { Button } from 'components/ui/button';

import { ConstantContactForm } from './constant_contact_form';
import { IContactInstructions } from './icontact_instructions';
import { FIELD_LABELS } from './providers';
import type { FormValues, PendingAction, ProviderDisplay, ProviderEnum } from './types';

interface Props {
  selectedProvider: ProviderEnum;
  display: ProviderDisplay;
  route: string;
  iContactAppId: string | null;
  values: FormValues;
  setValues: React.Dispatch<React.SetStateAction<FormValues>>;
  submitting: boolean;
  formValid: boolean;
  error: boolean;
  success: boolean;
  onCancel: () => void;
  requestAction: (actionLabel: PendingAction['actionLabel'], run: () => void) => void;
  connectProvider: (provider: ProviderEnum) => void;
}

export function ProviderForm({
  selectedProvider,
  display,
  route,
  iContactAppId,
  values,
  setValues,
  submitting,
  formValid,
  error,
  success,
  onCancel,
  requestAction,
  connectProvider,
}: Props) {
  const constantContactFormRef = useRef<HTMLFormElement>(null);

  return (
    <div>
      <p>
        <strong>Connect your {display.label} and Buildout accounts below:</strong>
      </p>

      {error && (
        <Alert className="mb-3" severity="destructive">
          Oops! The credentials you entered were not correct. Please review the form and try again.
        </Alert>
      )}
      {success && (
        <Alert className="mb-3" severity="success">
          Successfully authenticated. Please wait ...
        </Alert>
      )}

      {display.showIContactInstructions && <IContactInstructions iContactAppId={iContactAppId} />}

      {display.flow === 'credentials' ? (
        <>
          {display.fields.map((field) => {
            const id = `email-credential-${field}`;

            return (
              <Field className="form-group" key={field}>
                <Field.Label htmlFor={id}>{display.fieldLabels?.[field] ?? FIELD_LABELS[field]}</Field.Label>
                <Input
                  id={id}
                  type={field === 'password' ? 'password' : 'text'}
                  value={values[field]}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => (
                    setValues(prev => ({ ...prev, [field]: e.target.value }))
                  )}
                />
              </Field>
            );
          })}
          <div className="row mt-4">
            <div className="col-12 text-end">
              <Button className="me-2" variant="ghost" onClick={onCancel}>Cancel</Button>
              <Button
                disabled={!formValid || submitting}
                variant="primary"
                onClick={() => requestAction('Change', () => connectProvider(selectedProvider))}
              >
                Connect Accounts
              </Button>
            </div>
          </div>
        </>
      ) : (
        <div className="row">
          <div className="col-12">
            <p>
              Click the link below to begin the authentication process for Constant Contact. Once complete, you will
              be redirected back to Buildout.
            </p>
            <ConstantContactForm formRef={constantContactFormRef} route={route}>
              <div className="text-end">
                <Button className="me-2" type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => requestAction('Change', () => constantContactFormRef.current?.submit())}
                >
                  Connect Your Account
                </Button>
              </div>
            </ConstantContactForm>
          </div>
        </div>
      )}
    </div>
  );
}
